import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { BookOpenCheck, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

export default function LoginPage() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await login(email, password);
      nav("/");
    } catch (ex) {
      setErr(errMsg(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid-paper flex items-center px-5">
      <div className="w-full max-w-sm mx-auto fade-up">
        <div className="flex items-center gap-2 mb-10">
          <div className="h-9 w-9 bg-slate-900 text-yellow-300 grid place-items-center"><BookOpenCheck size={18} /></div>
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-slate-500">Daily Balance Book</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">Your books,<br />balanced daily.</h1>
        <p className="text-sm text-slate-500 mt-4 mb-10">Sign in to open your ledgers.</p>
        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <Field label="Email">
            <input data-testid="login-email-input" type="email" required autoComplete="email" value={email}
              onChange={(e) => setEmail(e.target.value)} className="login-input" />
          </Field>
          <Field label="Password">
            <input data-testid="login-password-input" type="password" required autoComplete="current-password" value={password}
              onChange={(e) => setPassword(e.target.value)} className="login-input" />
          </Field>
          {err && <p data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2">{err}</p>}
          <button data-testid="login-submit-btn" disabled={busy}
            className="w-full h-12 bg-slate-900 text-white font-semibold tracking-wide hover:bg-slate-800 active:scale-[0.99] transition-[background-color,transform] flex items-center justify-center gap-2 disabled:opacity-60">
            {busy && <Loader2 className="animate-spin" size={16} />} Sign in
          </button>
        </form>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">{label}</span>
    <div className="mt-1.5 [&_.login-input]:w-full [&_.login-input]:h-12 [&_.login-input]:px-3 [&_.login-input]:bg-white [&_.login-input]:border [&_.login-input]:border-slate-300 [&_.login-input]:outline-none [&_.login-input:focus]:border-slate-900 [&_.login-input]:transition-colors">
      {children}
    </div>
  </label>
);
